import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  type CollectionReference,
  type FirestoreError,
} from "firebase/firestore";
import { firestore } from "@/helpers/firebase/firebase";
import { userDocumentConverter } from "@kekkle/shared/convertors/web/user-document.converter";
import type { UserDocument } from "@kekkle/shared/types/documents/user-document/user-document";
import { handleFirestoreException } from "@/helpers/exceptions/handler";

/**
 * Type for updating user document that allows null values for nullable fields
 * (Firestore accepts null but not undefined)
 */
export type UserDocumentUpdate = Partial<
  Omit<UserDocument, "_id" | "birthday">
> & {
  birthday?: Date | null;
};

/**
 * Return the users collection reference with converter
 */
function userCollection(): CollectionReference<UserDocument> {
  return collection(firestore, "users").withConverter(userDocumentConverter);
}

/**
 * Get user document from firestore
 * @param userUuid - User's UUID
 * @returns User document with proper Date conversion
 */
export async function fetchUserDoc(
  userUuid: string,
): Promise<UserDocument | null> {
  const docSnapshot = await getDoc(doc(userCollection(), userUuid));
  if (!docSnapshot.exists()) {
    return null;
  }
  return docSnapshot.data();
}

/**
 * Type for creating a new user document
 */
export type CreateUserDocData = {
  name: string;
  profile_picture: string;
  birthday?: Date | null;
};

/**
 * Create a new user document (uses setDoc, safe for non-existent docs)
 * @param userUuid - User's UUID
 * @param data - Initial user data
 */
export async function createUserDoc(
  userUuid: string,
  data: CreateUserDocData,
): Promise<void> {
  try {
    const docReference = doc(userCollection(), userUuid);
    await setDoc(docReference, data);
  } catch (e) {
    handleFirestoreException({ e: e as FirestoreError });
    throw e;
  }
}

/**
 * Update user document
 * @param userUuid - User's UUID
 * @param data - Partial user data to update (null is acceptable for nullable fields)
 */
export async function updateUserDoc(
  userUuid: string,
  data: UserDocumentUpdate,
): Promise<void> {
  try {
    const docReference = doc(userCollection(), userUuid);
    await updateDoc(docReference, data);
  } catch (e) {
    handleFirestoreException({ e: e as FirestoreError });
    throw e;
  }
}
